import { z } from 'zod';
/**
 * The permission vocabulary: what an org-defined role is allowed to do.
 *
 * Two consumers must agree on this exactly - the API compiles a role into
 * `ForbiddenException`s and SQL predicates, and the web app renders the
 * permission matrix an admin edits. When those drift, the UI shows a switch
 * that controls nothing, or hides one that does. So there is one list, here,
 * in the shared package.
 *
 * ## Scope is the permission's value, not a flag beside it
 *
 * The obvious model is booleans with a separate "sees only their own records"
 * switch bolted on. That needs a second migration the moment anyone wants
 * per-resource visibility - and per-resource is what actually gets asked for
 * ("each person handles their own customers, but everyone sees the item list").
 *
 * So `view` / `edit` / `delete` take a scope rather than a boolean:
 *
 *     'none'  no access
 *     'own'   only records this user owns or created
 *     'all'   every record in the org
 *
 * `create` and `manage` stay boolean - creating a record you will then own is
 * not a scoped question. Adding `'team'` later is a value in this union and one
 * new branch in the predicate builder. No migration.
 *
 * ## Deny by default
 *
 * An absent resource, an absent action, an unparseable blob and an unknown key
 * all resolve to `'none'` / `false`. A resource added in a future release must
 * be invisible to every existing custom role until an admin grants it, rather
 * than silently granted to everyone the moment it ships. `resolvePermissions`
 * never throws - it falls back to deny-all, because a permission layer that
 * fails open is worse than one that fails loudly.
 *
 * ## Only actions with a real enforcement point
 *
 * Each resource declares the actions it supports, limited to what the API can
 * actually refuse. A switch in the matrix that enforces nothing is a lie told
 * to the admin who turns it off. When an endpoint lands, its action is added in
 * the same commit.
 *
 * ## Money resources are not CRM records
 *
 * Two rules here have no equivalent in a CRM and are the reason this file is
 * not a copy of FormDesk's:
 *
 *   - Issued documents are not editable the way a lead is. `invoice.edit`
 *     governs drafts; changing a SENT invoice is `invoice.manage`, because it
 *     restates a figure a customer has already seen.
 *   - `banking` and `report` default to admin-only. A bank balance and a P&L
 *     are the two things a business owner least expects every member to see.
 */
// ── Actions ───────────────────────────────────────────────────────────────
export const PERMISSION_SCOPES = ['none', 'own', 'all'];
/** Actions whose value is a scope. */
export const SCOPED_ACTIONS = ['view', 'edit', 'delete'];
/** Actions whose value is a plain yes/no. */
export const FLAG_ACTIONS = ['create', 'manage'];
export function isScopedAction(action) {
    return SCOPED_ACTIONS.includes(action);
}
/** Scope ordering, for the no-privilege-escalation check. */
const SCOPE_RANK = { none: 0, own: 1, all: 2 };
export const PERMISSION_RESOURCES = [
    // ── Sales ──
    { key: 'customer', label: 'Customers', group: 'Sales', actions: ['view', 'create', 'edit', 'delete'] },
    {
        key: 'invoice',
        label: 'Invoices',
        group: 'Sales',
        actions: ['view', 'create', 'edit', 'delete', 'manage'],
        hint: 'Edit covers drafts. Manage is needed to change, void or write off an invoice that has ' +
            'already been sent - that restates a figure the customer has seen.',
    },
    {
        key: 'proforma',
        label: 'Proforma invoices',
        group: 'Sales',
        actions: ['view', 'create', 'edit', 'delete', 'manage'],
        hint: 'A proforma is a demand for payment before the supply, not a tax invoice - it reaches no ' +
            'GST return and is owed by nobody. Manage covers converting one into a real invoice, which ' +
            'also needs permission to create invoices.',
    },
    {
        key: 'estimate',
        label: 'Estimates and quotes',
        group: 'Sales',
        actions: ['view', 'create', 'edit', 'delete', 'manage'],
        hint: 'Manage covers converting an accepted estimate into an invoice.',
    },
    {
        key: 'creditNote',
        label: 'Credit notes',
        group: 'Sales',
        actions: ['view', 'create', 'edit', 'delete', 'manage'],
        hint: 'A credit note reverses money already invoiced. Manage covers refunding one in cash.',
    },
    {
        key: 'payment',
        label: 'Payments received',
        group: 'Sales',
        actions: ['view', 'create', 'edit', 'delete'],
        hint: 'Recording what a customer has paid, and allocating it across their invoices.',
    },
    // ── Purchases ──
    { key: 'vendor', label: 'Vendors', group: 'Purchases', actions: ['view', 'create', 'edit', 'delete'] },
    { key: 'bill', label: 'Bills', group: 'Purchases', actions: ['view', 'create', 'edit', 'delete', 'manage'] },
    {
        key: 'expense',
        label: 'Expenses',
        group: 'Purchases',
        actions: ['view', 'create', 'edit', 'delete'],
        hint: 'Own lets someone file and see their own expenses without seeing everyone else’s.',
    },
    {
        key: 'attachment',
        label: 'Attachments and receipts',
        group: 'Purchases',
        actions: ['view', 'create', 'delete'],
        hint: 'Receipts, signed copies and bank screenshots kept against a record. Delete is separate ' +
            'because removing the proof of an expense is a different act from adding it - and it is ' +
            'the one an auditor would ask about.',
    },
    // ── Money ──
    {
        key: 'banking',
        label: 'Bank and cash accounts',
        group: 'Money',
        actions: ['view', 'create', 'edit', 'delete', 'manage'],
        hint: 'View exposes account balances and every transaction on them. Manage covers importing ' +
            'statements, matching transactions and recording transfers.',
    },
    // ── Catalogue ──
    { key: 'item', label: 'Items and services', group: 'Catalogue', actions: ['view', 'create', 'edit', 'delete'] },
    {
        key: 'tag',
        label: 'Tags',
        group: 'Catalogue',
        actions: ['view', 'create', 'edit', 'delete'],
        hint: 'Tags are shared by everyone in the business. Anyone who can see a record can put an ' +
            'existing tag on it; create is separate because inventing a label is how a tag list turns ' +
            'into forty near-duplicates, and delete is separate again because deleting a tag removes it ' +
            'from every record that carries it.',
    },
    {
        key: 'tax',
        label: 'Taxes',
        group: 'Catalogue',
        actions: ['view', 'manage'],
        hint: 'Manage covers adding rates and switching GST on. Changing tax affects every future document.',
    },
    // ── Work ──
    { key: 'project', label: 'Projects and timesheets', group: 'Sales', actions: ['view', 'create', 'edit', 'delete'] },
    // ── Insights ──
    {
        key: 'report',
        label: 'Reports and dashboard',
        group: 'Insights',
        actions: ['view'],
        hint: 'Own shows only the numbers for records this member owns. Everything shows the whole business.',
    },
    {
        key: 'audit',
        label: 'Activity log',
        group: 'Insights',
        actions: ['view'],
        hint: 'Own restricts the log to what this user did. Everything shows every member.',
    },
    {
        key: 'review',
        label: 'Review and queries',
        group: 'Insights',
        actions: ['view', 'manage'],
        hint: 'View runs the review checks over the books. Manage covers raising a query against a ' +
            'record, answering one, and marking it resolved.',
    },
    {
        key: 'gstReturn',
        label: 'GST returns',
        group: 'Insights',
        actions: ['view'],
        hint: 'Assembling and exporting GSTR-1 and GSTR-3B. The return restates the whole period, so it ' +
            'exposes every figure in it regardless of who owns the records.',
    },
    // ── Configuration ──
    {
        key: 'documentSettings',
        label: 'Templates and numbering',
        group: 'Configuration',
        actions: ['view', 'manage'],
        hint: 'Invoice templates, numbering series, payment terms and reminder rules.',
    },
    {
        key: 'periodLock',
        label: 'Period locks',
        group: 'Configuration',
        actions: ['view', 'manage'],
        hint: 'Manage covers freezing a filed period, and reopening one. A locked period refuses every ' +
            'edit dated inside it, which is what makes a filed return stay true.',
    },
    { key: 'settings', label: 'Book settings', group: 'Configuration', actions: ['view', 'manage'] },
    // ── Book ──
    {
        key: 'member',
        label: 'Members and roles',
        group: 'Book',
        actions: ['view', 'manage'],
        hint: 'Manage covers inviting people, changing their role, and editing roles themselves.',
    },
    { key: 'apiKey', label: 'API keys', group: 'Book', actions: ['view', 'manage'] },
    {
        key: 'billing',
        label: 'Subscription',
        group: 'Book',
        actions: ['view', 'manage'],
        hint: 'Manage covers paying for the licence and buying seats.',
    },
];
const RESOURCE_BY_KEY = new Map(PERMISSION_RESOURCES.map((r) => [r.key, r]));
export function resourceSupports(resource, action) {
    const def = RESOURCE_BY_KEY.get(resource);
    return !!def && def.actions.includes(action);
}
// ── The stored shape ──────────────────────────────────────────────────────
const scopeSchema = z.enum(PERMISSION_SCOPES);
const resourcePermsSchema = z
    .object({
    view: scopeSchema.optional(),
    edit: scopeSchema.optional(),
    delete: scopeSchema.optional(),
    create: z.boolean().optional(),
    manage: z.boolean().optional(),
})
    .strict();
/**
 * `v` is the on-disk format version. A future change to the vocabulary can
 * migrate stored blobs rather than guess at them; anything without a recognised
 * version resolves to deny-all instead of being read under today's rules.
 */
export const permissionSetSchema = z
    .object({
    v: z.literal(1),
    perms: z.record(z.string(), resourcePermsSchema),
})
    .strict();
/** The deny-everything set. Also the parse fallback. */
export const EMPTY_PERMISSIONS = { v: 1, perms: {} };
/**
 * Parse a stored blob into a usable set, never throwing.
 *
 * Accepts a string as well as an object because production is MariaDB, where
 * Drizzle's `json()` is LONGTEXT and mysql2 returns the raw string rather than
 * a parsed object. Handling it here means no caller can forget - and the cost
 * of forgetting on this particular column is a role that silently denies
 * everything.
 *
 * Anything unparseable, wrongly shaped, or of an unknown version returns
 * deny-all. A corrupted permission blob must not become an open door.
 */
export function resolvePermissions(raw) {
    let value = raw;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return EMPTY_PERMISSIONS;
        try {
            value = JSON.parse(trimmed);
        }
        catch {
            // Malformed JSON in the column. Deny-all is the safe reading; the boot
            // seeder rewrites system roles, so this self-heals for those.
            return EMPTY_PERMISSIONS;
        }
    }
    const parsed = permissionSetSchema.safeParse(value);
    return parsed.success ? parsed.data : EMPTY_PERMISSIONS;
}
// ── Reading a set ─────────────────────────────────────────────────────────
/** The scope this set grants. Unknown resource or unsupported action -> 'none'. */
export function scopeOf(set, resource, action) {
    if (!resourceSupports(resource, action))
        return 'none';
    return set.perms[resource]?.[action] ?? 'none';
}
/** Whether this set grants a boolean action. */
export function hasFlag(set, resource, action) {
    if (!resourceSupports(resource, action))
        return false;
    return set.perms[resource]?.[action] === true;
}
/**
 * Does this set permit the action at all?
 *
 * For scoped actions this is "not none" - it answers "may they do this to
 * something?", not "may they do this to THIS record". Row-level questions go
 * through `scopeOf` and a SQL predicate, because answering them needs the row.
 */
export function can(set, resource, action) {
    return isScopedAction(action)
        ? scopeOf(set, resource, action) !== 'none'
        : hasFlag(set, resource, action);
}
/**
 * Is `candidate` entirely within `granter`?
 *
 * The no-privilege-escalation rule. Without it, any role holding
 * `member.manage` can write itself a new role with every permission set and
 * assign it - which makes every other restriction decorative. Checked whenever
 * a role is created or edited, against the editor's own permissions.
 */
export function isSubsetOf(candidate, granter) {
    for (const resource of PERMISSION_RESOURCES) {
        for (const action of resource.actions) {
            if (isScopedAction(action)) {
                if (SCOPE_RANK[scopeOf(candidate, resource.key, action)] > SCOPE_RANK[scopeOf(granter, resource.key, action)]) {
                    return false;
                }
            }
            else if (hasFlag(candidate, resource.key, action) &&
                !hasFlag(granter, resource.key, action)) {
                return false;
            }
        }
    }
    return true;
}
/**
 * Strip anything the vocabulary does not define, so an admin editing a role
 * cannot store keys the resolver will silently ignore - and so a stored blob
 * stays readable after a resource is renamed or retired.
 */
export function normalisePermissions(set) {
    const perms = {};
    for (const resource of PERMISSION_RESOURCES) {
        const current = set.perms[resource.key];
        if (!current)
            continue;
        const kept = {};
        for (const action of resource.actions) {
            if (isScopedAction(action)) {
                const scope = current[action];
                if (scope && scope !== 'none')
                    kept[action] = scope;
            }
            else if (current[action] === true) {
                kept[action] = true;
            }
        }
        if (Object.keys(kept).length)
            perms[resource.key] = kept;
    }
    return { v: 1, perms };
}
// ── System roles ──────────────────────────────────────────────────────────
export const SYSTEM_ROLE_KEYS = ['admin', 'member', 'auditor'];
export function isSystemRoleKey(key) {
    return SYSTEM_ROLE_KEYS.includes(key);
}
/** Everything, everywhere. Built from the vocabulary so it can never fall behind it. */
function fullPermissions() {
    const perms = {};
    for (const resource of PERMISSION_RESOURCES) {
        const entry = {};
        for (const action of resource.actions) {
            if (isScopedAction(action))
                entry[action] = 'all';
            else
                entry[action] = true;
        }
        perms[resource.key] = entry;
    }
    return { v: 1, perms };
}
/**
 * What a `member` can do out of the box.
 *
 * Unlike FormDesk's equivalent - which had to transcribe behaviour that already
 * existed, bugs included - this is a fresh decision, so it is stated as one.
 * The shape is: members run the day-to-day sales cycle, and do not touch money
 * at rest, configuration, or anything irreversible.
 *
 *   Sales records      full create/edit, NO delete. Deleting a customer with
 *                      invoices against them is not a mistake anyone should be
 *                      able to make casually.
 *   proforma           full use, INCLUDING manage. A proforma is a price offer,
 *                      not a tax document - it reaches no return and is owed by
 *                      nobody, so raising and converting one is ordinary sales
 *                      work. The conversion still hits `invoice.create`, so the
 *                      tax document at the end of it is gated as it always was.
 *   invoice.manage     DENIED. Editing, voiding or writing off an invoice that
 *                      has already been sent restates a figure a customer has
 *                      seen and an accountant may have filed.
 *   expense            scoped to 'own'. The common shape of an expense feature
 *                      is "everyone files theirs, an admin reviews all of them",
 *                      and 'own' is that shape without any extra machinery.
 *   banking            DENIED entirely. A member does not need the bank balance
 *                      to raise an invoice, and it is the single most sensitive
 *                      surface in the app.
 *   report.view        'own'. Their own numbers, not the org's revenue.
 *   audit.view         'own'.
 *   catalogue          read-only. Items and taxes drive every figure on every
 *                      document; editing them is an admin decision.
 *   configuration      denied.
 *   review, gstReturn,
 *   periodLock         DENIED. All three are the accountant's surface, not the
 *                      staff member's. A return restates every figure in the
 *                      period - including the ones `report.view: 'own'` was
 *                      meant to keep from this member - and a period lock
 *                      decides whether anyone may edit a filed month at all.
 *
 * An admin can widen any of these by editing the member role or writing a
 * custom one. The point of the default is that it is the safe reading, not the
 * convenient one - a business owner should have to opt IN to their staff seeing
 * the bank account.
 */
function defaultMemberPermissions() {
    return {
        v: 1,
        perms: {
            // The sales cycle: full use, no deletes, no restating a sent document.
            customer: { view: 'all', create: true, edit: 'all' },
            invoice: { view: 'all', create: true, edit: 'all' },
            proforma: { view: 'all', create: true, edit: 'all', manage: true },
            estimate: { view: 'all', create: true, edit: 'all', manage: true },
            creditNote: { view: 'all', create: true, edit: 'all' },
            payment: { view: 'all', create: true, edit: 'all' },
            project: { view: 'all', create: true, edit: 'all' },
            // Purchases: vendors and bills are visible, expenses are personal.
            vendor: { view: 'all', create: true, edit: 'all' },
            bill: { view: 'all', create: true, edit: 'all' },
            expense: { view: 'own', create: true, edit: 'own', delete: 'own' },
            // Attach freely, delete nothing. Someone filing an expense must be able
            // to put the receipt on it - that is the same act. Removing proof from a
            // record afterwards is not, and it is the version an auditor asks about.
            attachment: { view: 'all', create: true },
            // Catalogue: read-only. These drive every figure on every document.
            item: { view: 'all' },
            tax: { view: 'all' },
            // Tags are the exception in this group, because inventing one while
            // filing an expense is the whole point of tags - a member who has to ask
            // an administrator for a label will simply not label anything. Delete is
            // withheld: it takes the tag off every record that carries it.
            tag: { view: 'all', create: true, edit: 'all' },
            // Insights: their own numbers.
            report: { view: 'own' },
            audit: { view: 'own' },
            // Deliberately absent, i.e. denied: banking, review, gstReturn,
            // documentSettings, periodLock, settings, member, apiKey, billing.
        },
    };
}
/**
 * What an auditor can do: look. At everything.
 *
 * The CA, the auditor, the person filing GST or tax: they need every list,
 * every record, every receipt, the bank, the returns, the activity log -
 * and they must not be able to change a figure, because the figures they
 * are checking are the ones a change would restate. So `view: 'all'` on
 * every resource that has a view action (`report.view` at 'all' is what
 * makes the P&L and the returns answer at all), and no other action
 * anywhere. `member`, `apiKey`, `billing` and `admin` are absent: who else
 * is in the book, and how the AI agent reaches it, are not the audit.
 *
 * Built from the vocabulary, like `fullPermissions`, so a resource added
 * later is visible to an auditor by default - which is the right default
 * for a seat whose whole purpose is to see - while its writes stay denied
 * by construction.
 */
function auditorPermissions() {
    const perms = {};
    for (const resource of PERMISSION_RESOURCES) {
        if (AUDITOR_NEVER_SEES.has(resource.key))
            continue;
        if (resource.actions.includes('view'))
            perms[resource.key] = { view: 'all' };
    }
    return { v: 1, perms };
}
const AUDITOR_NEVER_SEES = new Set(['member', 'apiKey', 'billing', 'admin']);
/**
 * The two roles every org is seeded with. Every boot adds to each org's stored
 * copy any resource these definitions have that the copy lacks
 * (`RoleResyncBootstrap` in the API), so a new resource reaches existing orgs'
 * system roles - while custom roles correctly see it as denied until an admin
 * grants it.
 */
export function systemRole(key) {
    if (key === 'admin') {
        return {
            key: 'admin',
            name: 'Administrator',
            description: 'Full access to everything, including bank accounts, settings and billing.',
            permissions: fullPermissions(),
        };
    }
    if (key === 'auditor') {
        return {
            key: 'auditor',
            name: 'Auditor',
            description: 'Sees everything in the book - invoices, receipts, payments, bank accounts, returns, the ' +
                'activity log - and changes nothing. Cannot connect an AI agent.',
            permissions: auditorPermissions(),
        };
    }
    return {
        key: 'member',
        name: 'Member',
        description: 'Raises invoices, proformas and estimates, files their own expenses. Cannot see bank ' +
            'accounts, change a sent invoice, or edit settings.',
        permissions: defaultMemberPermissions(),
    };
}
export const SYSTEM_ROLES = SYSTEM_ROLE_KEYS.map(systemRole);
//# sourceMappingURL=permissions.js.map